import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { getCodeInputProps, normalizeRecoveryCode } from '../lib/mfa';
import { describeMfaError } from '../lib/mfaError';
import type { MfaMethod } from '../types';
import { MethodToggle } from './MethodToggle';

const codeSchema = z
	.object({
		method: z.enum(['totp', 'recovery_code']),
		code: z.string().trim().min(1, 'Enter your code'),
	})
	.superRefine((values, ctx) => {
		if (values.method === 'totp' && !/^\d{6}$/.test(values.code.trim())) {
			ctx.addIssue({
				code: 'custom',
				path: ['code'],
				message: 'Enter the 6-digit code from your app',
			});
		}
	});

type CodeFormValues = z.infer<typeof codeSchema>;

export interface MfaCodeSubmit {
	method: MfaMethod;
	code: string;
}

interface MfaCodeFormProps {
	methods: MfaMethod[];
	onSubmit: (input: MfaCodeSubmit) => void;
	isPending?: boolean;
	error?: unknown;
	submitLabel?: string;
	onCancel?: () => void;
	cancelLabel?: string;
	/** Focus the code input on mount (login challenge). */
	autoFocus?: boolean;
}

/**
 * Reusable second-factor input. Used by the login challenge, the
 * reauthentication challenge and the enrollment confirmation step.
 */
export function MfaCodeForm({
	methods,
	onSubmit,
	isPending = false,
	error,
	submitLabel = 'Verify',
	onCancel,
	cancelLabel = 'Cancel',
	autoFocus = false,
}: MfaCodeFormProps) {
	const defaultMethod = methods[0] ?? 'totp';

	const form = useForm<CodeFormValues>({
		resolver: zodResolver(codeSchema),
		defaultValues: { method: defaultMethod, code: '' },
	});

	const method = form.watch('method');
	const errorInfo = describeMfaError(error);

	// Reset stale codes when the method changes.
	useEffect(() => {
		form.setValue('code', '');
		form.clearErrors('code');
	}, [form, method]);

	const handleSubmit = (values: CodeFormValues) => {
		const code =
			values.method === 'recovery_code'
				? normalizeRecoveryCode(values.code)
				: values.code.trim();

		onSubmit({ method: values.method, code });
	};

	return (
		<Form {...form}>
			<form
				onSubmit={form.handleSubmit(handleSubmit)}
				className="grid gap-5"
				noValidate
			>
				{methods.length > 1 ? (
					<MethodToggle
						methods={methods}
						value={method}
						disabled={isPending}
						onChange={(next) => form.setValue('method', next)}
					/>
				) : null}

				<FormField
					control={form.control}
					name="code"
					render={({ field }) => (
						<FormItem>
							<FormLabel>
								{method === 'totp' ? 'Authentication code' : 'Recovery code'}
							</FormLabel>
							<FormControl>
								<Input
									{...field}
									{...getCodeInputProps(method)}
									autoFocus={autoFocus}
									disabled={isPending}
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>

				{error ? (
					<p role="alert" className="text-destructive text-sm">
						{errorInfo.message}
					</p>
				) : null}

				<div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
					{onCancel ? (
						<Button
							type="button"
							variant="ghost"
							onClick={onCancel}
							disabled={isPending}
						>
							{cancelLabel}
						</Button>
					) : null}
					<Button type="submit" disabled={isPending}>
						{isPending ? <Loader2 className="animate-spin" /> : null}
						{submitLabel}
					</Button>
				</div>
			</form>
		</Form>
	);
}
